import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Check, X, Trash2, Plus, Package, ShoppingBag, Recycle, Users, IndianRupee,
  Pencil, ShieldCheck, Truck, AlertOctagon, Flag, MapPin, Phone,
} from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import SiteSettingsPanel from "@/components/SiteSettingsPanel";
import AdminPayouts from "@/components/AdminPayouts";
import AdminUsers from "@/components/AdminUsers";
import ProductEditForm from "@/components/ProductEditForm";
import AdminVerifications from "@/components/AdminVerifications";
import AdminShipping from "@/components/AdminShipping";
import AdminDisputes from "@/components/AdminDisputes";
import AdminReports from "@/components/AdminReports";
import AdminCoupons from "@/components/AdminCoupons";
import MyNegotiations from "@/components/MyNegotiations";

const CATS = ["jackets","tees","jeans","tote-bags","tops","thrifted"];

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("thrifts");
  const [thrifts, setThrifts] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [np, setNp] = useState({ name: "", category: "jackets", price: 0, description: "", images: [], sizes: "", colors: "", stock: 10, is_custom: true, tags: "" });

  const refresh = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/thrift/all", { params: { status: "pending" } }).then(({ data }) => setThrifts(data));
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/orders/all").then(({ data }) => setOrders(data));
  };
  useEffect(() => { if (user?.role === "admin") refresh(); }, [user]);

  if (authLoading) return <div className="p-16 text-center font-mono-b" data-testid="admin-loading">LOADING...</div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== "admin") return <Navigate to="/" />;

  const approve = async (id) => {
    await api.post(`/thrift/${id}/approve`);
    toast.success("Approved & live");
    refresh();
  };
  const reject = async (id) => {
    await api.post(`/thrift/${id}/reject`);
    toast.success("Rejected");
    refresh();
  };
  const delProduct = async (id) => {
    if (!window.confirm("Delete product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const createProduct = async (e) => {
    e.preventDefault();
    if (np.images.length === 0) { toast.error("Upload at least one image"); return; }
    const payload = {
      name: np.name, category: np.category, price: Number(np.price), description: np.description,
      images: np.images,
      sizes: np.sizes.split(",").map(s => s.trim()).filter(Boolean),
      colors: np.colors.split(",").map(s => s.trim()).filter(Boolean),
      stock: Number(np.stock), is_custom: np.is_custom,
      tags: np.tags.split(",").map(s => s.trim()).filter(Boolean),
    };
    try {
      await api.post("/products", payload);
      toast.success("Product added");
      setShowNew(false);
      setNp({ name: "", category: "jackets", price: 0, description: "", images: [], sizes: "", colors: "", stock: 10, is_custom: true, tags: "" });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const cards = [
    { icon: <IndianRupee />, label: "REVENUE", value: `₹${stats?.revenue ?? 0}`, color: "bg-[#D4FF00] text-black" },
    { icon: <ShoppingBag />, label: "ORDERS", value: stats?.paid_orders ?? 0, color: "bg-[#FF007F] text-white" },
    { icon: <Package />, label: "PRODUCTS", value: stats?.total_products ?? 0, color: "bg-black text-[#D4FF00]" },
    { icon: <Recycle />, label: "PENDING THRIFTS", value: stats?.pending_thrifts ?? 0, color: "bg-[#FF4500] text-white" },
    { icon: <ShieldCheck />, label: "KYC PENDING", value: stats?.pending_verifications ?? 0, color: "bg-white text-black" },
    { icon: <AlertOctagon />, label: "DISPUTES", value: stats?.open_disputes ?? 0, color: "bg-[#FF007F] text-white" },
    { icon: <Flag />, label: "REPORTS", value: stats?.open_reports ?? 0, color: "bg-black text-[#D4FF00]" },
    { icon: <Users />, label: "USERS", value: stats?.total_users ?? 0, color: "bg-white text-black" },
  ];

  const tabs = [
    ["thrifts", "PENDING THRIFTS", stats?.pending_thrifts],
    ["verifications", "KYC", stats?.pending_verifications],
    ["shipping", "SHIPPING", null],
    ["disputes", "DISPUTES", stats?.open_disputes],
    ["reports", "REPORTS", stats?.open_reports],
    ["bargains", "BARGAINS", null],
    ["coupons", "COUPONS", null],
    ["products", "PRODUCTS", null],
    ["orders", "ORDERS", null],
    ["payouts", "PAYOUTS", null],
    ["users", "USERS", null],
    ["site", "SITE", null],
  ];

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="admin-page">
      <div className="admin-hero border-2 border-black brutal-shadow p-6 mb-8 relative overflow-hidden">
        <div className="flex items-start justify-between flex-wrap gap-4 relative z-10">
          <div>
            <span className="sticker">ADMIN // BACKEND ACCESS</span>
            <h1 className="font-display text-5xl md:text-7xl mt-2 leading-none">CONTROL <span className="text-[#FF007F]">ROOM</span></h1>
            <p className="font-mono-b text-xs mt-2 opacity-70">Everything that moves through OUTLAW-D, live.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3" data-testid="admin-stats">
        {cards.map((c) => (
          <div key={c.label} className={`stat-card ${c.color} border-2 border-black brutal-shadow-sm p-4`}>
            <div className="opacity-70">{c.icon}</div>
            <p className="font-mono-b text-[11px] mt-2 font-bold uppercase">{c.label}</p>
            <p className="font-display text-4xl mt-1 leading-none">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-2 border-b-2 border-black mt-10 flex-wrap">
        {tabs.map(([k, l, count]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2 font-mono-b font-bold uppercase border-2 border-b-0 border-black -mb-[2px] ${tab === k ? "bg-black text-[#D4FF00]" : "bg-white"}`}>
            {l}
            {count > 0 && (
              <span className={`ml-2 text-[10px] px-1.5 border-2 border-black ${tab === k ? "bg-[#FF007F] text-white" : "bg-[#FF007F] text-white"}`}>{count}</span>
            )}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "thrifts" && (
          thrifts.length === 0 ? <p className="font-mono-b p-8 text-center border-2 border-dashed border-black" data-testid="admin-no-pending">No pending thrifts.</p> : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {thrifts.map((t) => (
                <div key={t.submission_id} data-testid={`admin-thrift-${t.submission_id}`} className="bg-white border-2 border-black brutal-shadow-sm">
                  {t.images?.[0] && <img src={t.images[0]} alt="" className="w-full aspect-[4/3] object-cover border-b-2 border-black" />}
                  <div className="p-3">
                    <h3 className="font-display text-xl">{t.name}</h3>
                    <p className="font-mono-b text-xs text-black/60">{t.category} · SIZE {t.size} · {t.condition}</p>
                    <p className="font-mono-b text-sm mt-2">{t.description}</p>
                    <p className="font-mono-b font-bold mt-2">₹{t.price}</p>
                    <p className="font-mono-b text-xs text-black/50 mt-1">by {t.seller_email}</p>
                    <div className="mt-3 flex gap-2">
                      <button data-testid={`approve-${t.submission_id}`} onClick={() => approve(t.submission_id)} className="flex-1 bg-[#D4FF00] border-2 border-black py-2 font-mono-b font-bold uppercase text-sm hover-nudge"><Check className="inline w-4 h-4" /> Approve</button>
                      <button data-testid={`reject-${t.submission_id}`} onClick={() => reject(t.submission_id)} className="flex-1 bg-[#FF007F] text-white border-2 border-black py-2 font-mono-b font-bold uppercase text-sm hover-nudge"><X className="inline w-4 h-4" /> Reject</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "verifications" && <AdminVerifications />}
        {tab === "shipping" && <AdminShipping />}
        {tab === "disputes" && <AdminDisputes />}
        {tab === "reports" && <AdminReports />}
        {tab === "bargains" && <MyNegotiations adminMode={true} />}
        {tab === "coupons" && <AdminCoupons />}

        {tab === "products" && (
          <div>
            <button data-testid="admin-new-product" onClick={() => setShowNew(!showNew)} className="bg-black text-[#D4FF00] border-2 border-black brutal-shadow hover-nudge px-4 py-2 font-mono-b font-bold uppercase mb-4"><Plus className="inline w-4 h-4" /> New Product</button>
            {showNew && (
              <form onSubmit={createProduct} className="bg-white border-2 border-black brutal-shadow p-4 grid md:grid-cols-2 gap-3 mb-6" data-testid="admin-new-product-form">
                {[
                  ["name","NAME"],["price","PRICE (₹)","number"],
                  ["sizes","SIZES (comma sep)"],
                  ["colors","COLORS (comma sep)"],["stock","STOCK","number"],
                  ["tags","TAGS (comma sep)"],
                ].map(([k, l, t]) => (
                  <div key={k}>
                    <label className="text-xs font-mono-b font-bold uppercase">{l}</label>
                    <input data-testid={`admin-new-${k}`} type={t || "text"} value={np[k]} onChange={(e) => setNp({ ...np, [k]: e.target.value })} className="brutal-input w-full mt-1" required={k === "name" || k === "price"} />
                  </div>
                ))}
                <div>
                  <label className="text-xs font-mono-b font-bold uppercase">CATEGORY</label>
                  <select data-testid="admin-new-category" value={np.category} onChange={(e) => setNp({ ...np, category: e.target.value })} className="brutal-input w-full mt-1 uppercase">
                    {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <label className="flex items-center gap-2 font-mono-b text-sm">
                  <input type="checkbox" checked={np.is_custom} onChange={(e) => setNp({ ...np, is_custom: e.target.checked })} /> Custom-made (uncheck for thrifted)
                </label>
                <div className="md:col-span-2">
                  <label className="text-xs font-mono-b font-bold uppercase">DESCRIPTION</label>
                  <textarea rows={3} data-testid="admin-new-description" value={np.description} onChange={(e) => setNp({ ...np, description: e.target.value })} className="brutal-input w-full mt-1" required />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-mono-b font-bold uppercase block mb-2">IMAGES</label>
                  <ImageUpload value={np.images} onChange={(imgs) => setNp({ ...np, images: imgs })} max={8} testIdPrefix="admin-product" />
                </div>
                <button data-testid="admin-new-submit" type="submit" className="md:col-span-2 bg-[#D4FF00] border-2 border-black brutal-shadow hover-nudge py-2 font-mono-b font-bold uppercase">CREATE</button>
              </form>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {products.map((p) => (
                editingId === p.product_id ? (
                  <div key={p.product_id} className="md:col-span-2 lg:col-span-4">
                    <ProductEditForm
                      product={p}
                      isAdmin={true}
                      onSaved={() => { setEditingId(null); refresh(); }}
                      onCancel={() => setEditingId(null)}
                    />
                  </div>
                ) : (
                  <div key={p.product_id} className="bg-white border-2 border-black p-3 brutal-shadow-sm" data-testid={`admin-product-${p.product_id}`}>
                    <img src={p.images?.[0]} alt="" className={`w-full aspect-square object-cover border-2 border-black ${p.stock <= 0 ? "grayscale" : ""}`} />
                    <p className="font-display text-lg mt-2">{p.name}</p>
                    <p className="font-mono-b text-xs text-black/60">{p.category} · ₹{p.price} · stock {p.stock}{p.stock <= 0 && " · SOLD"}</p>
                    <div className="mt-2 flex gap-1">
                      <button data-testid={`admin-edit-${p.product_id}`} onClick={() => setEditingId(p.product_id)} className="flex-1 bg-black text-[#D4FF00] border-2 border-black py-1 font-mono-b text-xs font-bold uppercase flex items-center justify-center gap-1"><Pencil className="w-3 h-3" /> EDIT</button>
                      <button data-testid={`admin-del-${p.product_id}`} onClick={() => delProduct(p.product_id)} className="flex-1 bg-[#FF007F] text-white border-2 border-black py-1 font-mono-b text-xs font-bold uppercase flex items-center justify-center gap-1"><Trash2 className="w-3 h-3" /> DEL</button>
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        )}

        {tab === "orders" && (
          orders.length === 0 ? <p className="font-mono-b p-8 text-center border-2 border-dashed border-black">No orders yet.</p> : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.order_id} className="bg-white border-2 border-black brutal-shadow-sm p-4" data-testid={`admin-order-${o.order_id}`}>
                  <div className="flex justify-between flex-wrap gap-2">
                    <div>
                      <p className="font-mono-b text-xs text-black/60">{o.order_id}</p>
                      <p className="font-display text-xl">₹{o.total} · <span className={o.status === "paid" || o.status === "shipped" ? "text-green-700" : "text-[#FF007F]"}>{o.status.toUpperCase()}</span></p>
                      <p className="font-mono-b text-xs">{o.user_email}</p>
                      {o.awb && <p className="font-mono-b text-xs mt-1"><Truck className="inline w-3 h-3" /> {o.courier} · {o.awb}</p>}
                      {o.coupon_code && o.discount > 0 && (
                        <p className="font-mono-b text-[11px] mt-1"><b>{o.coupon_code}</b> ({o.coupon_percent}% off) · -₹{o.discount}</p>
                      )}
                    </div>
                    <p className="font-mono-b text-xs">{new Date(o.created_at).toLocaleString()}</p>
                  </div>
                  {o.items?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {o.items.map((it, i) => (
                        <div key={`${it.product_id}-${i}`} className="flex gap-2 items-center border-2 border-black bg-[#F4F4F0] p-2">
                          {it.image && <img src={it.image} alt="" className="w-10 h-10 object-cover" />}
                          <div className="font-mono-b text-[11px]"><b>{it.name}</b>{it.size ? ` · ${it.size}` : ""}{it.color ? ` · ${it.color}` : ""} · ₹{it.subtotal}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {o.address && (
                    <div className="mt-3 border-2 border-black bg-[#D4FF00] p-3" data-testid={`admin-order-address-${o.order_id}`}>
                      <p className="font-mono-b text-[10px] font-bold uppercase flex items-center gap-1"><MapPin className="w-3 h-3" /> SHIP TO</p>
                      <p className="font-display text-lg leading-tight mt-1">{o.address.name}</p>
                      <p className="font-mono-b text-xs">{o.address.line1}</p>
                      <p className="font-mono-b text-xs">{o.address.city}, {o.address.state} — {o.address.pincode}</p>
                      <p className="font-mono-b text-xs mt-1 inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {o.address.phone}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        )}

        {tab === "site" && <SiteSettingsPanel />}
        {tab === "payouts" && <AdminPayouts />}
        {tab === "users" && <AdminUsers />}
      </div>
    </div>
  );
}
